<div class="workflows-panel" [ngSwitch]="view">

    <div class="workflows-list" *ngSwitchCase="'list'">
        <div class="workflows-toolbar" *ngIf="listLoaded && !panelEmpty && !listLoadFailed">
            <h3 class="workflows-heading">Workflows <span class="workflows-count">({{ workflows.length }})</span></h3>
            <button type="button" class="btn-pill" (click)="openCreate()">
                <i class="ph-bold ph-plus icon-16" aria-hidden="true"></i>
                <span>New workflow</span>
            </button>
        </div>

        <div class="workflows-skeleton" *ngIf="!listLoaded" role="status" aria-live="polite" aria-busy="true">
            <span class="visually-hidden">Loading workflows</span>
            <div class="workflow-row workflows-skeleton-row" *ngFor="let row of skeletonRows; trackBy: trackByIndex" aria-hidden="true">
                <span class="gable-skeleton-line gable-skeleton-line--md"></span>
                <span class="gable-skeleton-line gable-skeleton-line--lg"></span>
                <span class="gable-skeleton-line gable-skeleton-line--sm"></span>
            </div>
        </div>

        <p class="workflows-load-error" *ngIf="listLoadFailed && !loading" role="alert">
            Couldn't load your workflows.
            <button type="button" class="link-btn" (click)="loadList()">Try again</button>
        </p>

        <ul class="workflow-list" *ngIf="listLoaded && !panelEmpty && !listLoadFailed">
            <li class="workflow-row"
                *ngFor="let w of workflows; trackBy: trackById"
                [class.is-paused]="w.status === 'paused'"
                [class.is-busy]="isBusy(w)">
                <div class="workflow-main">
                    <div class="workflow-title-row">
                        <span class="workflow-title">{{ w.title }}</span>
                        <app-status-chip [label]="statusLabel(w)" [color]="statusColor(w)"></app-status-chip>
                    </div>
                    <div class="workflow-cadence">{{ cadence(w) }}</div>
                    <div class="workflow-outcomes" data-test="outcomes-line">{{ outcomes(w) }}</div>
                    <span class="workflow-skill-chip" *ngIf="skillName(w) as skill" data-test="skill-chip">
                        <i class="ph-bold ph-sparkle icon-12" aria-hidden="true"></i>
                        <span>{{ skill }}</span>
                    </span>
                    <div class="workflow-next">{{ nextRun(w) }}</div>
                    <div class="workflow-running" *ngIf="isRunning(w); else lastRunLine" role="status" data-test="running-block">
                        <i class="ph-bold ph-circle-notch icon-14 workflow-running-icon" aria-hidden="true"></i>
                        <span>Running now. You will see what it found when it finishes.</span>
                    </div>
                    <ng-template #lastRunLine>
                        <div class="workflow-last" [class.is-error]="lastRunFailed(w)">
                            <i class="ph-bold icon-14"
                               [ngClass]="lastRunFailed(w) ? 'ph-warning-circle' : 'ph-clock-counter-clockwise'"
                               aria-hidden="true"></i>
                            <span>{{ lastRunSummary(w) }}</span>
                        </div>
                    </ng-template>
                </div>

                <div class="workflow-actions">
                    <button type="button"
                            class="icon-btn"
                            *ngIf="isSuperUser"
                            data-test="run-now"
                            matTooltip="Run now"
                            [disabled]="isBusy(w) || isRunning(w)"
                            (click)="runNow(w)"
                            aria-label="Run now">
                        <i class="ph-bold ph-play icon-16" aria-hidden="true"></i>
                    </button>
                    <button type="button"
                            class="icon-btn"
                            [matMenuTriggerFor]="rowMenu"
                            [disabled]="isBusy(w)"
                            aria-label="Workflow options">
                        <i class="ph-bold ph-dots-three icon-16" aria-hidden="true"></i>
                    </button>
                    <mat-menu #rowMenu="matMenu" xPosition="before" class="gable-menu-panel">
                        <button mat-menu-item data-test="view-runs" (click)="openRuns(w)">
                            <i class="ph-bold ph-clock-counter-clockwise icon-muted icon-16" aria-hidden="true"></i>
                            <span>View runs</span>
                        </button>
                        <button mat-menu-item (click)="togglePause(w)">
                            <i class="ph-bold icon-muted icon-16" [ngClass]="w.status === 'paused' ? 'ph-play-circle' : 'ph-pause-circle'" aria-hidden="true"></i>
                            <span>{{ w.status === 'paused' ? 'Resume' : 'Pause' }}</span>
                        </button>
                        <button mat-menu-item (click)="openEdit(w)">
                            <i class="ph-bold ph-pencil-simple icon-muted icon-16" aria-hidden="true"></i>
                            <span>Edit</span>
                        </button>
                        <button mat-menu-item class="menu-item-danger" (click)="confirmRemove(w)">
                            <i class="ph-bold ph-trash icon-16" aria-hidden="true"></i>
                            <span>Delete</span>
                        </button>
                    </mat-menu>
                </div>
            </li>
        </ul>

        <p class="workflows-helper" *ngIf="listLoaded && !panelEmpty && !listLoadFailed">
            Each run writes a summary. Suggestions wait for your approval in Activity; nothing is changed until you approve it.
        </p>

        <div class="workflows-empty" *ngIf="panelEmpty">
            <span class="workflows-empty-icon" aria-hidden="true">
                <i class="ph-bold ph-flow-arrow icon-20" aria-hidden="true"></i>
            </span>
            <h3 class="workflows-empty-title">No workflows yet</h3>
            <p class="workflows-empty-copy">
                Tell Gable what to do and when. It asks a question or two, then runs on schedule and tells you what it found.
            </p>
            <button type="button" class="btn-pill" (click)="openCreate()">
                <i class="ph-bold ph-plus icon-16" aria-hidden="true"></i>
                <span>New workflow</span>
            </button>
        </div>
    </div>

    <div class="workflows-editor" *ngSwitchCase="'editor'">
        <button type="button" class="back-link" (click)="backToList()">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>All workflows</span>
        </button>
        <app-gable-workflow-editor [workflow]="editing"
                                   (saved)="onEditorSaved($event)"
                                   (cancel)="backToList()"></app-gable-workflow-editor>
    </div>

    <div class="workflows-runs" *ngSwitchCase="'runs'">
        <button type="button" class="back-link" (click)="backToList()">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>All workflows</span>
        </button>

        <ng-container *ngIf="runsWorkflow as w">
            <div class="runs-header">
                <div class="runs-heading-block">
                    <h3 class="runs-heading">{{ w.title }}</h3>
                    <div class="runs-cadence">{{ cadence(w) }}</div>
                    <p class="runs-summary" *ngIf="w.summary" data-test="runs-summary">{{ w.summary }}</p>
                </div>
                <button type="button"
                        class="btn-pill"
                        *ngIf="isSuperUser && runs.length > 0"
                        data-test="runs-run-now"
                        [disabled]="isBusy(w) || isRunning(w)"
                        (click)="runNow(w)">
                    <i class="ph-bold ph-play icon-16" aria-hidden="true"></i>
                    <span>Run now</span>
                </button>
            </div>

            <ul class="run-list" *ngIf="runs.length > 0">
                <li class="run-row"
                    *ngFor="let run of runs; trackBy: trackById"
                    [class.is-expanded]="isRunExpanded(run)"
                    [class.is-running]="runIsInFlight(run)">
                    <button type="button"
                            class="run-row-toggle"
                            data-test="run-toggle"
                            [attr.aria-expanded]="isRunExpanded(run)"
                            (click)="toggleRun(run)">
                        <span class="run-when" [matTooltip]="runWhenRelative(run)">{{ runWhen(run) }}</span>
                        <app-status-chip [label]="runStatusLabel(run)" [color]="runStatusColor(run)"></app-status-chip>
                        <span class="run-outcome" [class.is-error]="run.status === 'failed'" data-test="run-outcome">{{ runOutcome(run) }}</span>
                        <span class="run-duration">{{ runDuration(run) }}</span>
                        <i class="ph-bold icon-muted icon-16 run-caret" [ngClass]="isRunExpanded(run) ? 'ph-caret-up' : 'ph-caret-down'" aria-hidden="true"></i>
                    </button>
                    <div class="run-detail" *ngIf="isRunExpanded(run)" data-test="run-detail">
                        <div class="markdown-body run-summary" *ngIf="run.summary; else noSummary" [innerHTML]="run.summary | markdown: 'final'"></div>
                        <ng-template #noSummary>
                            <p class="run-no-summary">{{ runIsInFlight(run) ? 'The summary appears here when the run finishes.' : 'No summary was written for this run.' }}</p>
                        </ng-template>
                        <button type="button" class="link-btn" *ngIf="(run.candidatesStaged ?? 0) > 0" data-test="see-suggestions" (click)="openActivity()">
                            See suggestions in Activity
                        </button>
                    </div>
                </li>
            </ul>

            <div class="runs-skeleton" *ngIf="runsLoading && runs.length === 0" role="status" aria-live="polite" aria-busy="true">
                <span class="visually-hidden">Loading runs</span>
                <div class="run-row runs-skeleton-row" *ngFor="let row of skeletonRows; trackBy: trackByIndex" aria-hidden="true">
                    <span class="gable-skeleton-line gable-skeleton-line--lg"></span>
                </div>
            </div>

            <p class="workflows-load-error" *ngIf="runsLoadFailed && !runsLoading" role="alert">
                Couldn't load the runs.
                <button type="button" class="link-btn" (click)="loadMoreRuns()">Try again</button>
            </p>

            <div class="runs-empty" *ngIf="!runsLoading && !runsLoadFailed && runs.length === 0" data-test="runs-empty">
                <button type="button"
                        class="btn-pill"
                        *ngIf="isSuperUser"
                        data-test="runs-run-now"
                        [disabled]="isBusy(w) || isRunning(w)"
                        (click)="runNow(w)">
                    <i class="ph-bold ph-play icon-16" aria-hidden="true"></i>
                    <span>Run now</span>
                </button>
                <p class="runs-empty-copy">This workflow has not run yet.</p>
            </div>

            <button type="button"
                    class="btn-secondary runs-load-more"
                    *ngIf="runsHaveMore && !runsLoadFailed"
                    data-test="load-more"
                    [disabled]="runsLoading"
                    (click)="loadMoreRuns()">
                {{ runsLoading ? 'Loading' : 'Load more' }}
            </button>
        </ng-container>
    </div>
</div>
